import { useWaitingParticipants } from '@daily-co/daily-react';
import { useSubjectLanguageVariants } from '@features/user';
import { Check as CheckIcon, ContentCopy as CopyIcon, Send as SendIcon } from '@mui/icons-material';
import { Button, CircularProgress, Collapse, Stack, Typography } from '@mui/material';

interface WaitingParticipant {
  id: string;
  name: string;
}

interface WaitingParticipantsProps {
  isOwner: boolean;
  hasRemoteParticipants?: boolean;
  onCopyLink?: () => void;
  isCopyingLink?: boolean;
  linkCopied?: boolean;
  onSendToPatient?: () => void;
}

export function WaitingParticipants({
  isOwner,
  hasRemoteParticipants,
  onCopyLink,
  isCopyingLink,
  linkCopied,
  onSendToPatient,
}: WaitingParticipantsProps) {
  const { singularCap } = useSubjectLanguageVariants();
  const { waitingParticipants, grantAccess, denyAccess } = useWaitingParticipants();
  const participants = waitingParticipants as WaitingParticipant[];

  if (!isOwner) return null;

  // Participants waiting — compact admit/deny row
  if (participants.length > 0) {
    return (
      <Collapse in>
        <Stack spacing={0.5}>
          {participants.map(p => (
            <Stack
              key={p.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ bgcolor: 'grey.800', p: 1, borderRadius: 2 }}
            >
              <Typography variant="body2" sx={{ color: 'common.white', flex: 1 }}>
                <strong>{p.name || 'Guest'}</strong> wants to join the call
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => grantAccess(p.id)}
                aria-label={`Admit ${p.name || 'guest'}`}
                sx={{ textTransform: 'none' }}
              >
                Admit
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => denyAccess(p.id)}
                aria-label={`Deny ${p.name || 'guest'}`}
                sx={{
                  color: 'grey.400',
                  borderColor: 'rgba(255,255,255,0.3)',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.5)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Deny
              </Button>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    );
  }

  // No participants waiting — compact invite buttons if provider is alone
  if (!onCopyLink || hasRemoteParticipants) return null;

  return (
    <Stack direction="row" spacing={1} sx={{ bgcolor: 'grey.800', p: 1, borderRadius: 2 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={
          isCopyingLink ? (
            <CircularProgress size={14} />
          ) : linkCopied ? (
            <CheckIcon sx={{ fontSize: 16 }} />
          ) : (
            <CopyIcon sx={{ fontSize: 16 }} />
          )
        }
        onClick={onCopyLink}
        disabled={isCopyingLink || linkCopied}
        sx={{
          color: 'common.white',
          borderColor: 'rgba(255,255,255,0.3)',
          textTransform: 'none',
          '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.5)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
      >
        {linkCopied ? 'Copied!' : 'Copy invite link'}
      </Button>
      {onSendToPatient && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<SendIcon sx={{ fontSize: 16 }} />}
          onClick={onSendToPatient}
          sx={{
            color: 'common.white',
            borderColor: 'rgba(255,255,255,0.3)',
            textTransform: 'none',
            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
          }}
        >
          {`Send invite to ${singularCap}`}
        </Button>
      )}
    </Stack>
  );
}
